import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

export const CREATE_INTENT_PARAM = 'create';

export type CreateIntentKind = 'notification' | 'event' | 'action';

/**
 * Lets the Hub's "+ Create" menu open a creation UI that lives inside a tab,
 * without reaching into that tab's local state: the menu writes
 * `?create=<kind>` and the tab opens its own form in response.
 *
 * The param is cleared once handled so a refresh or a later tab switch does not
 * reopen the form.
 */
export function useCreateIntent(
  kind: CreateIntentKind,
  onRequested: () => void
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(CREATE_INTENT_PARAM) === kind;
  const handled = useRef(false);

  useEffect(() => {
    if (!requested) {
      handled.current = false;
      return;
    }
    if (handled.current) return;
    handled.current = true;

    onRequested();
    setSearchParams(
      (params) => {
        params.delete(CREATE_INTENT_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [requested, onRequested, setSearchParams]);
}
